import { Grid, TextField } from "@mui/material";
import React, { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import { OUTCOMES, SCREEN_PATHS } from "../../../constants";
import { FlowScreenWrapper } from "./FlowScreenWrapper";
import { ScreenFlowFormButtons } from "./ScreenFlowFormButtons";
import { useAuthContext } from "../../../AuthContextWrapper";
import { objectVals2String } from "../../../utils/objectVals2String";

interface Values {
  firstName: string;
  middleName: string;
  lastName: string;
}

interface Props {
  role: string;
  title?: string;
  screenData?: any;
}

export const NameScreen = ({ role, title, screenData }: Props): JSX.Element => {
  const navigate = useNavigate();
  const { flow } = useAuthContext() // estate
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  // console.log(`${currentScreen} screenData: ${JSON.stringify(screenData)}`);
  const userProfile = screenData?.length && screenData[0];

  // from signUp screen
  const signUpAttrs = JSON.parse(
    window.sessionStorage.getItem("AtticusSignUp") || "{}"
  );

  const firstName =
    (userProfile?.first_name as string) || signUpAttrs["first"] || "";
  const middleName =
    (userProfile?.middle_name as string) || signUpAttrs["middle"] || "";
  const lastName =
    (userProfile?.last_name as string) || signUpAttrs["last"] || "";
  // const isNew = !firstName && !middleName && !lastName;

  // https://formik.org/docs/api/formik
  const formikInit = {
    values: {
      firstName,
      middleName,
      lastName,
    },
    schema: Yup.object().shape({
      firstName: Yup.string().required(),
      middleName: Yup.string(),
      lastName: Yup.string().required(),
    }),
  };

  const handleSubmit = async (
    values: Values,
    { setSubmitting, setStatus }: FormikHelpers<Values>
  ) => {
    setStatus("");
    try {
      if (!currentScreen) throw Error("couldn't determine currentScreen");

      // TODO: save to database
      console.log(`save name to ${role} (executive or attorney) field`);

      // how to convert values object into simple Record object?
      // TODO: add middle_name field to user_profile
      // if saving to user id is "me"
      // if saving to estates id is context.estate.id
      if (
        !(await flow?.saveData(currentScreen, "me", {
          first_name: values.firstName,
          middle_name: values.middleName,
          last_name: values.lastName,
        }))
      ) {
        throw new Error("could not save data");
      }
      flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    } catch (error) {
      setStatus("Sorry, error: " + ((error as Error)?.message || ""));
      // console.error(error);
      setSubmitting(false);
    }
  };

  return currentScreen ? (
    <FlowScreenWrapper title={title}>
      <Formik
        initialValues={formikInit.values}
        validationSchema={formikInit.schema}
        onSubmit={handleSubmit}
        key={objectVals2String(formikInit.values)}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          status,
        }) => (
          <form onSubmit={handleSubmit}>
            <Grid
              container
              spacing={1}
              sx={{
                my: 1,
              }}
            >
              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.firstName && errors.firstName)}
                  fullWidth
                  helperText={touched.firstName && errors.firstName}
                  label="First name"
                  margin="normal"
                  name="firstName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.firstName}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.middleName && errors.middleName)}
                  fullWidth
                  helperText={touched.middleName && errors.middleName}
                  label="Middle name"
                  margin="normal"
                  name="middleName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.middleName}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.lastName && errors.lastName)}
                  fullWidth
                  helperText={touched.lastName && errors.lastName}
                  label="Last name"
                  margin="normal"
                  name="lastName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.lastName}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <ScreenFlowFormButtons
                  isSubmitting={isSubmitting}
                  status={status}
                  onBack={() => navigate(SCREEN_PATHS.home)}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
